import { NAuthClientError } from '@nauth-toolkit/client';
import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query is the only owner of server state in this app. Nothing fetched
 * is ever copied into useState — a mirrored copy is a second source of truth that
 * the socket feed cannot invalidate.
 */

const MAX_RETRIES = 2;
const THIRTY_SECONDS = 30_000;
/**
 * How long an unmounted screen's data survives.
 *
 * Longer than it looks like it needs to be, on purpose. Past `gcTime` a screen
 * you navigate back to has NOTHING cached and renders its loading state from
 * scratch; inside it, it paints instantly from cache and refetches behind the
 * paint. At five minutes, moving between Board, Sprints and Backlog over the
 * course of a morning kept dropping screens just long enough that coming back
 * to one meant waiting for it again — which is the whole of what "switching
 * between menu items is slow" feels like from the outside. Thirty minutes
 * costs a few hundred kilobytes of retained JSON per tab.
 *
 * `staleTime` is untouched: the data is still considered stale after thirty
 * seconds and still refetched. This only decides whether the refetch happens
 * behind something or instead of it.
 */
const THIRTY_MINUTES = 30 * 60_000;

/** Exported as a factory so tests can build an isolated client per case. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: THIRTY_SECONDS,
        gcTime: THIRTY_MINUTES,

        // The socket feed is the invalidation signal (see features/realtime), so
        // refetching on every focus is redundant traffic. The socket's own
        // reconnect handler covers the case where we missed events while away.
        refetchOnWindowFocus: false,

        retry: (failureCount, error) => {
          // 401/403/404 will not become true by asking a second time. 429, 5xx
          // and network failures (no status) might, so they fall through.
          if (error instanceof NAuthClientError && error.statusCode !== undefined) {
            if (error.statusCode < 500 && error.statusCode !== 429) return false;
          }
          return failureCount < MAX_RETRIES;
        },
      },

      mutations: {
        // Writes are not idempotent: a retried "post a request" is a duplicate
        // request in the client's list. Retry is a decision for each call site.
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();
